import { Link, useParams } from "react-router";
import { Alerta, Adjuntos, Badge, Button, Card, Dialog, Row, Select, Spinner, Stack } from "@/components";
import { TIPOS_IMAGEN } from "@/lib/adjuntos";
import { formatMoneda } from "@/lib/format";
import { coeficienteAInput, etiquetaPiso, formatM2, formatPorcentajeUnidad, nombreUnidad } from "@/lib/unidades";
import { TONO_ESTADO_UNIDAD, etiquetaEstado } from "@/lib/ventas";
import { FormularioUnidad } from "./FormularioUnidad";
import { useUnidadTab } from "./useUnidadTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="contents">
      <dt className="text-texto-suave">{etiqueta}</dt>
      <dd className="break-words">{valor}</dd>
    </div>
  );
}

/** `key={id}` fuerza el remount al cambiar de unidad (mismo motivo que GastoTab). */
export function UnidadTab() {
  const { id } = useParams();
  return <Ficha key={id} />;
}

function Ficha() {
  const t = useUnidadTab();
  const u = t.unidad;

  if (t.cargando) {
    return (
      <div className="grid place-items-center py-10 text-primario">
        <Spinner />
      </div>
    );
  }
  if (t.errorCarga || !u) {
    return (
      <Stack espacio={3}>
        <Alerta texto={t.errorCarga ?? "No existe esa unidad"} />
        <Link to="/unidades" className="text-primario underline-offset-2 hover:underline">
          Volver a unidades
        </Link>
      </Stack>
    );
  }

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <Link to="/unidades" className="text-chico text-primario underline-offset-2 hover:underline">
          ← Unidades
        </Link>
        <Row justificar="entre" envolver espacio={3}>
          <Row espacio={2} envolver>
            <h1 className="text-pantalla font-light tracking-[-0.03em]">{nombreUnidad(u)}</h1>
            <Badge>{etiquetaPiso(u.piso)}</Badge>
            <Badge tono={TONO_ESTADO_UNIDAD[u.estado_venta]}>{etiquetaEstado(u)}</Badge>
            {u.venta && (
              <Link to={`/ventas/${u.venta.id}`} className="text-chico text-primario underline-offset-2 hover:underline">
                {`Venta ${u.venta.numero}`}
              </Link>
            )}
          </Row>
          {t.esAdmin && (
            <Row espacio={2}>
              <Button variante="secundario" onClick={t.abrirEditar}>
                Editar
              </Button>
              <Button variante="fantasma" onClick={t.abrirBorrar}>
                Borrar
              </Button>
            </Row>
          )}
        </Row>
      </Stack>

      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
          <Dato etiqueta="Cubierta" valor={formatM2(u.sup_cubierta_m2)} />
          <Dato etiqueta="Semicubierta" valor={formatM2(u.sup_semicubierta_m2)} />
          <Dato etiqueta="Descubierta" valor={formatM2(u.sup_descubierta_m2)} />
          <Dato etiqueta="Total" valor={formatM2(u.sup_total_m2)} />
          {u.coef_altura !== null && (
            <Dato
              etiqueta="m² ponderados"
              valor={`${formatM2(u.m2_ponderados)}${u.coef_cubierta !== null ? ` (coeficientes propios: ${[u.coef_cubierta, u.coef_semicubierta ?? "0", u.coef_descubierta ?? "0"].map(coeficienteAInput).join(" · ")})` : ""}`}
            />
          )}
          <Dato
            etiqueta="m² homologados"
            valor={u.coef_altura === null ? formatM2(u.m2_homologados) : `${formatM2(u.m2_homologados)} (coef. altura ${coeficienteAInput(u.coef_altura)})`}
          />
          <Dato etiqueta="% fideicomiso" valor={u.porcentaje_fideicomiso === null ? "— (fuera de venta)" : formatPorcentajeUnidad(u.porcentaje_fideicomiso)} />
          <Dato etiqueta="Ambientes" valor={u.ambientes === null ? "—" : String(u.ambientes)} />
          <Dato etiqueta="Orientación" valor={u.orientacion ?? "—"} />
          {u.precio_sugerido_usd && <Dato etiqueta="Precio sugerido" valor={`${formatMoneda(u.precio_sugerido_usd, "USD")} (m² homologados × precio base)`} />}
          {u.precio_lista_usd && <Dato etiqueta="Precio de lista" valor={formatMoneda(u.precio_lista_usd, "USD")} />}
          {u.fuera_de_venta && <Dato etiqueta="Motivo" valor={u.motivo_fuera_de_venta ?? "—"} />}
          <Dato etiqueta="Notas" valor={u.notas ?? "—"} />
        </dl>
        {t.puedeUsarSugerido && (
          <Stack espacio={2} className="mt-4">
            <div>
              <Button variante="secundario" tamanio="sm" cargando={t.usando} onClick={() => void t.usarSugerido()}>
                Usar como precio de lista
              </Button>
            </div>
            <Alerta texto={t.errorUsar} />
          </Stack>
        )}
      </Card>

      <Card>
        <Stack espacio={4}>
          <Stack espacio={1}>
            <h2 className="text-titulo font-light tracking-tight">Para los fiduciantes</h2>
            <p className="text-chico text-texto-suave">El plano y los renders se ven en Proyecto, al tocar la unidad en la maqueta o en la lista.</p>
          </Stack>
          <Adjuntos etiqueta="Plano" adjuntos={u.planos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} tipos={TIPOS_IMAGEN} {...t.archivos("plano")} />
          <Adjuntos etiqueta="Renders" adjuntos={u.renders} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} tipos={TIPOS_IMAGEN} {...t.archivos("renders")} />
          {t.conTipologia && <SeccionTipologia t={t} />}
        </Stack>
      </Card>

      <Adjuntos etiqueta="Otros archivos" adjuntos={u.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} {...t.archivos("adjuntos")} />

      <Dialog abierto={t.dialogo === "editar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Editar unidad">
        <FormularioUnidad form={t.form} cambiar={t.cambiar} errores={t.errores} total={t.total} onSubmit={(e) => void t.guardar(e)} onCancelar={t.cerrar} guardando={t.guardando} error={t.errorGuardar} />
      </Dialog>

      <Dialog abierto={t.dialogo === "borrarTipologia"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar tipología">
        <Stack espacio={4}>
          <p>{`¿Borrar la tipología «${t.tipologia?.nombre ?? ""}»? Sus renders se borran y sus unidades quedan sin tipología, con sus renders propios.`}</p>
          <Alerta texto={t.errorBorrarTipologia} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrandoTipologia} onClick={() => void t.confirmarBorrarTipologia()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={t.dialogo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar unidad">
        <Stack espacio={4}>
          <p>{`¿Borrar ${nombreUnidad(u)}? Sus archivos también se borran.`}</p>
          {u.venta && <p className="rounded-control bg-alerta-suave px-3 py-2 text-chico text-alerta">Esta unidad tiene una venta: no se puede borrar</p>}
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}

/** Tipología: los deptos iguales comparten estos renders (en Proyecto se suman a los propios de cada uno). */
function SeccionTipologia({ t }: { t: ReturnType<typeof useUnidadTab> }) {
  const tip = t.tipologia;
  return (
    <Stack espacio={3} className="border-t border-borde pt-4">
      <Stack espacio={1}>
        <h3 className="font-semibold">Tipología</h3>
        <p className="text-chico text-texto-suave">
          Los deptos iguales comparten los renders de su tipología. Escribí un nombre nuevo para crearla: se suman solos los de la misma letra con los mismos m² y ambientes.
        </p>
      </Stack>
      {t.esAdmin ? (
        <Select
          etiqueta="Tipología"
          opciones={t.opcionesTipologia}
          valor={tip ? String(tip.id) : ""}
          onCambio={(v) => void t.elegirTipologia(v)}
          onCrear={(nombre) => void t.crearTipologia(nombre)}
          textoCrear={(nombre) => `Crear la tipología «${nombre}»`}
          placeholder="Elegí una o escribí el nombre de una nueva"
          deshabilitado={t.guardandoTipologia}
        />
      ) : (
        <p className="text-chico">{tip ? `Tipología: ${tip.nombre}` : "Sin tipología"}</p>
      )}
      <Alerta texto={t.errorTipologia} />
      {tip && (
        <>
          <p className="text-chico text-texto-suave">{t.compartidaCon ? `Se comparte con ${t.compartidaCon}.` : "Por ahora es la única unidad con esta tipología."}</p>
          <Adjuntos
            etiqueta={`Renders de la tipología ${tip.nombre}`}
            adjuntos={tip.renders}
            rutaDescarga={t.rutaDescarga}
            puedeEditar={t.esAdmin}
            tipos={TIPOS_IMAGEN}
            {...t.archivos("tipologia")}
          />
          {t.esAdmin && (
            <div>
              <Button variante="fantasma" tamanio="sm" onClick={t.abrirBorrarTipologia}>
                Borrar la tipología
              </Button>
            </div>
          )}
        </>
      )}
    </Stack>
  );
}
